import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileCheck2, ClipboardCheck, Rocket, ListChecks, Sparkles, Coins, BadgeCheck, UserCheck, LifeBuoy } from 'lucide-react';
import SEOHead from '@/components/marketing/SEOHead';
import Hero from '@/components/marketing/Hero';
import CTASection from '@/components/marketing/CTASection';
import FormatCard from '@/components/marketing/FormatCard';
import VerticalCard from '@/components/marketing/VerticalCard';
import FeatureCard from '@/components/marketing/FeatureCard';
import FAQAccordion from '@/components/marketing/FAQAccordion';
import DecorBackground from '@/components/marketing/DecorBackground';
import AISection from '@/components/marketing/AISection';
import GlobalTrafficStrip from '@/components/marketing/GlobalTrafficStrip';
import Reveal from '@/components/marketing/Reveal';
import { Button } from '@/components/ui/button';
import { FORMATS, VERTICALS, FEATURE_GRID, WHY_DASHBOUND, HOW_IT_WORKS, TRACKING, FAQ_PREVIEW } from '@/data/site-content';
import { organizationJsonLd, websiteJsonLd, graph } from '@/lib/jsonld';

const GradKey = ({ from = '#22D3EE', via = '#3B82F6', to = '#8B5CF6', children }) => (
  <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(90deg, ${from}, ${via}, ${to})` }}>
    {children}
  </span>
);

export default function Home() {
  const homeJsonLd = graph(organizationJsonLd(), websiteJsonLd());
  return (
    <>
      <SEOHead
        path="/"
        jsonLd={homeJsonLd}
      />
      <Hero />

      <GlobalTrafficStrip />

      {/* About Ads Dashbound — entity-clarity paragraph for AI / search engines */}
      <section
        className="relative bg-[#06101F] text-white isolate overflow-hidden"
        data-testid="home-about-entity-section"
        aria-labelledby="home-about-entity-heading"
      >
        <DecorBackground variant="section" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
              About Ads Dashbound
            </span>
            <h2
              id="home-about-entity-heading"
              className="font-display mt-5 text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-[-0.025em] leading-[1.15] text-white"
            >
              The performance traffic platform behind <GradKey>Dashbound.media</GradKey>
            </h2>
            <p className="mt-5 text-[16px] sm:text-[17px] leading-[1.7] text-slate-300/90 max-w-3xl">
              <strong className="text-white font-semibold">Ads Dashbound</strong> is a performance traffic platform operated on{' '}
              <strong className="text-white font-semibold">Dashbound.media</strong>. It helps advertisers, agencies, and media buyers explore curated traffic formats such as{' '}
              <Link to="/ad-formats/zero-click" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">zero-click</Link>,{' '}
              <Link to="/ad-formats/domain-redirect" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">domain redirect</Link>,{' '}
              <Link to="/ad-formats/pop" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">pop</Link>,{' '}
              <Link to="/ad-formats/push" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">push</Link>,{' '}
              <Link to="/ad-formats/in-page-push" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">in-page push</Link>, and{' '}
              <Link to="/ad-formats/direct-click" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">direct click</Link>{' '}
              traffic with{' '}
              <Link to="/features/source-level-control" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">source-level controls</Link>,{' '}
              <Link to="/features/ai-traffic-intelligence" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">AI-assisted routing signals</Link>,{' '}
              <Link to="/features/fraud-filtering" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">fraud filtering</Link>, and{' '}
              <Link to="/features/top-up-model" className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors">top-up based campaign launch</Link>{' '}
              support.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button
                asChild
                data-testid="home-entity-apply-cta"
                className="btn-primary-gradient text-white rounded-xl px-6 py-5 text-[14px] font-semibold border-0"
              >
                <Link to="/apply" className="inline-flex items-center gap-2">
                  Apply as Advertiser <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="home-entity-about-cta"
                className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl px-6 py-5 text-[14px] font-semibold transition-colors backdrop-blur-md"
              >
                <Link to="/about" className="inline-flex items-center gap-2">
                  About Ads Dashbound
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AI-Powered Traffic Intelligence */}
      <AISection />

      {/* Traffic Formats */}
      <section className="relative bg-[#08111F] text-white isolate overflow-hidden" data-testid="home-formats-section">
        <DecorBackground variant="section" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-[#7DDCFF]/20 text-[12px] font-medium text-[#22D3EE]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#22D3EE]" /> Traffic Formats
                </span>
                <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05]">
                  Pick the <GradKey>right format</GradKey> for every offer
                </h2>
                <p className="mt-4 text-[17px] text-slate-300/85 leading-[1.65]">
                  Six performance formats covering direct-response, retargeting, and broad-reach scaling — with source-level controls on every one.
                </p>
              </div>
              <Button asChild variant="outline" className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl transition-colors backdrop-blur-md">
                <Link to="/ad-formats">View all formats <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FORMATS.map((f, i) => (
              <Reveal key={f.slug} delay={0.04 * i}>
                <FormatCard format={f} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Dashbound (light) */}
      <section className="bg-[#F5F7FB]" data-testid="home-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
          <Reveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/15 text-[12px] font-medium text-[#2563EB]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" /> Why Ads Dashbound
              </span>
              <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05] text-slate-900">
                Built for <GradKey from="#2563EB" via="#22D3EE" to="#8B5CF6">global performance</GradKey> teams
              </h2>
              <p className="mt-4 text-[17px] text-slate-600 leading-[1.65]">A curated supply, real human reviewers, and the levers that performance teams actually need to scale.</p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_DASHBOUND.map((w, i) => (
              <Reveal key={i} delay={0.04 * i}>
                <FeatureCard feature={{ icon: w.icon, title: w.title, text: w.text }} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Top-Up section: amber-accented */}
      <section className="relative bg-[#0E1A2D] text-white isolate overflow-hidden" data-testid="home-topup-section">
        <DecorBackground variant="section" />
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          aria-hidden
          style={{ background: 'radial-gradient(700px 380px at 80% 20%, rgba(251,191,36,0.12), transparent 65%), radial-gradient(700px 380px at 20% 80%, rgba(34,211,238,0.14), transparent 65%)' }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
          <Reveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-[#FBBF24]/30 text-[12px] font-medium text-[#FBBF24]">
                <Coins className="h-3 w-3" /> Funding Model
              </span>
              <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05]">
                Simple <GradKey from="#FBBF24" via="#22D3EE" to="#8B5CF6">Top-Up</GradKey> Based Advertising
              </h2>
              <p className="mt-4 text-[17px] text-slate-300/90 leading-[1.65]">
                No fixed subscription plans. No public pricing tables. Ads Dashbound works with approved advertisers through a flexible top-up model based on traffic format, GEO, budget, and campaign requirements.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 relative">
            <div
              className="hidden md:block absolute top-[44px] left-[10%] right-[10%] h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.45), rgba(34,211,238,0.45), rgba(139,92,246,0.45), transparent)' }}
              aria-hidden
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 relative">
              {[
                { n: 1, icon: FileCheck2, title: 'Apply First', text: 'Submit your advertiser application with offers, target GEOs, traffic format, and estimated daily budget. Takes ~3 minutes.', accent: '#FBBF24' },
                { n: 2, icon: ClipboardCheck, title: 'Get Reviewed', text: 'A real human reviews your offers for compliance, recommends formats and source rules, and shares a launch checklist.', accent: '#22D3EE' },
                { n: 3, icon: Rocket, title: 'Top Up & Launch', text: 'Approved advertisers receive onboarding instructions, top-up details, and managed launch support during the first 72 hours.', accent: '#8B5CF6' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.n} delay={0.06 * i}>
                    <div data-testid="top-up-step" className="relative rounded-2xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/10 p-7 backdrop-blur-xl lift-on-hover overflow-hidden h-full">
                      <div className="absolute top-0 left-0 right-0 h-px opacity-80" style={{ background: `linear-gradient(90deg, transparent, ${s.accent}, transparent)` }} aria-hidden />
                      <div className="flex items-center gap-3">
                        <span
                          className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white border"
                          style={{
                            background: `linear-gradient(135deg, ${s.accent}33, ${s.accent}10)`,
                            borderColor: `${s.accent}40`,
                            color: s.accent,
                            boxShadow: `0 0 22px ${s.accent}33`,
                          }}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="font-display text-[40px] font-bold leading-none font-mono-tabular" style={{ color: `${s.accent}33` }}>
                          0{s.n}
                        </span>
                      </div>
                      <h3 className="font-display mt-6 text-[22px] font-semibold tracking-[-0.025em] text-white">{s.title}</h3>
                      <p className="mt-3 text-[14.5px] text-slate-300/85 leading-relaxed">{s.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* 4 small benefit cards */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: BadgeCheck, label: 'No monthly subscription', tone: '#34D399' },
              { icon: Coins, label: 'Budget goes toward delivery', tone: '#FBBF24' },
              { icon: UserCheck, label: 'Manual advertiser review', tone: '#22D3EE' },
              { icon: LifeBuoy, label: 'Managed launch support', tone: '#8B5CF6' },
            ].map((b, i) => {
              const I = b.icon;
              return (
                <Reveal key={i} delay={0.04 * i}>
                  <div className="rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-xl p-4 flex items-center gap-3 lift-on-hover">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border" style={{ background: `${b.tone}1A`, borderColor: `${b.tone}40`, color: b.tone }}>
                      <I className="h-4 w-4" />
                    </span>
                    <span className="text-[13px] font-medium text-slate-200">{b.label}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl bg-white/[0.03] border border-[#FBBF24]/25 backdrop-blur-xl p-5 sm:p-6">
            <p className="text-[14px] text-slate-300 max-w-xl">
              No fixed pricing tables. Top-up minimums depend on format, GEO and vertical. Your account manager will share recommended ranges during onboarding.
            </p>
            <Button asChild className="btn-primary-gradient text-white rounded-xl px-5 py-5 text-[14px] font-semibold border-0 shrink-0" data-testid="topup-cta-button">
              <Link to="/apply" className="inline-flex items-center gap-2">Submit Application <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#F5F7FB]" data-testid="home-features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
          <Reveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/15 text-[12px] font-medium text-[#2563EB]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" /> Platform Features
              </span>
              <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05] text-slate-900">
                Everything <GradKey from="#2563EB" via="#22D3EE" to="#8B5CF6">performance teams</GradKey> expect
              </h2>
              <p className="mt-4 text-[17px] text-slate-600 leading-[1.65]">From source-level controls to GEO targeting and tracker compatibility — the toolkit you need to scale responsibly.</p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURE_GRID.map((f, i) => (
              <Reveal key={i} delay={0.03 * i}>
                <FeatureCard feature={f} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Verticals */}
      <section className="relative bg-[#08111F] text-white isolate overflow-hidden" data-testid="home-verticals-section">
        <DecorBackground variant="section" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div className="max-w-3xl">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-[#7DDCFF]/20 text-[12px] font-medium text-[#22D3EE]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#22D3EE]" /> Verticals
                </span>
                <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05]">
                  Built for <GradKey>global performance</GradKey> verticals
                </h2>
                <p className="mt-4 text-[17px] text-slate-300/85 leading-[1.65]">
                  Format and GEO recommendations tuned to the verticals where performance teams actually scale.
                </p>
              </div>
              <Button asChild variant="outline" className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl transition-colors backdrop-blur-md">
                <Link to="/verticals">All verticals <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VERTICALS.map((v, i) => (
              <Reveal key={v.slug} delay={0.04 * i}>
                <VerticalCard vertical={v} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works (5 steps) */}
      <section className="bg-[#F5F7FB]" data-testid="home-how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
          <Reveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/15 text-[12px] font-medium text-[#2563EB]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" /> How it works
              </span>
              <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05] text-slate-900">
                From <GradKey from="#2563EB" via="#22D3EE" to="#8B5CF6">application to launch</GradKey> in 5 steps
              </h2>
            </div>
          </Reveal>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-4">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.step} delay={0.04 * i}>
                <li data-testid="how-it-works-step" className="rounded-2xl bg-white border border-slate-900/10 p-5 lift-on-hover shadow-[0_8px_24px_rgba(2,6,23,0.04)] hover:shadow-[0_18px_45px_rgba(2,6,23,0.10)] h-full">
                  <div className="flex items-center justify-between">
                    <span
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white text-[14px] font-bold font-mono-tabular"
                      style={{ background: 'linear-gradient(135deg, #2563EB, #22D3EE 55%, #8B5CF6)' }}
                    >
                      {s.step}
                    </span>
                    <ListChecks className="h-4 w-4 text-slate-300" />
                  </div>
                  <h3 className="font-display mt-4 text-[15.5px] font-semibold tracking-[-0.02em] text-slate-900">{s.title}</h3>
                  <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{s.text}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Tracking strip */}
      <section className="relative bg-[#060B18] text-white border-t border-white/5 isolate overflow-hidden" data-testid="home-tracking-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Compatible with common tracking workflows</p>
          <div className="mt-7 overflow-hidden relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10" style={{ background: 'linear-gradient(90deg, #060B18, transparent)' }} />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10" style={{ background: 'linear-gradient(-90deg, #060B18, transparent)' }} />
            <div className="flex gap-12 marquee-track whitespace-nowrap items-center">
              {[...TRACKING, ...TRACKING].map((t, i) => (
                <span key={i} data-testid="tracking-logo" className="font-display text-[18px] sm:text-[22px] font-semibold tracking-[-0.02em] text-slate-400 hover:text-[#22D3EE] transition-colors">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <p className="mt-5 text-center text-[12px] text-slate-500">Workflow compatibility shown for reference. Not an official partnership claim.</p>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="bg-[#F5F7FB]" data-testid="home-faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <Reveal>
            <div className="text-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/15 text-[12px] font-medium text-[#2563EB]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" /> FAQ
              </span>
              <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05] text-slate-900">
                Common <GradKey from="#2563EB" via="#22D3EE" to="#8B5CF6">questions</GradKey>
              </h2>
            </div>
          </Reveal>
          <div className="mt-10 rounded-2xl bg-white border border-slate-900/10 px-6 shadow-[0_10px_30px_rgba(2,6,23,0.04)]">
            <FAQAccordion items={FAQ_PREVIEW} idPrefix="home-faq" />
          </div>
          <div className="mt-7 text-center">
            <Button asChild variant="outline" className="rounded-xl transition-colors">
              <Link to="/faq" className="inline-flex items-center gap-2">View all FAQs <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
